import { Box, Group, Stack, Text, Paper, Center } from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import React, { useMemo } from "react"; // 👈 Import useMemo

const DailySchedule = ({ timeStart, timeStop, disabled }) => {
  // Generate an array of hours from 0 to 23
  const hoursOfDay = Array.from({ length: 24 }, (_, i) => i);

  // Helper to convert "HH:mm" to a decimal number (e.g., "08:30" => 8.5)
  const getDecimalTime = (timeString) => {
    if (!timeString || typeof timeString !== "string") return NaN;
    const [hours, minutes] = timeString.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes)) return NaN;
    return hours + minutes / 60;
  };

  // Memoize decimal times to avoid recalculating on every render
  const startDecimal = useMemo(() => getDecimalTime(timeStart), [timeStart]);
  const stopDecimal = useMemo(() => getDecimalTime(timeStop), [timeStop]);

  // Function to calculate the fill % and offset % for an hour bar
  const getFillData = (hour, start, end) => {
    if (isNaN(start) || isNaN(end)) return { fill: 0, offset: 0 };

    const startHour = Math.floor(start);
    const stopHour = Math.floor(end);
    const startMinutes = start - startHour;
    const stopMinutes = end - stopHour;

    let fill = 0;
    let offset = 0;

    // Case 1: Standard interval (e.g., 08:30 to 16:45)
    if (start <= end) {
      if (hour < startHour || hour >= end) return { fill: 0, offset: 0 };
      if (hour > startHour && hour < stopHour) {
        fill = 1; // Full hour
      } else if (hour === startHour && hour === stopHour) {
        // Starts and ends in the same hour
        offset = startMinutes;
        fill = stopMinutes - startMinutes;
      } else if (hour === startHour) {
        // The first hour
        offset = startMinutes;
        fill = 1 - startMinutes;
      } else if (hour === stopHour) {
        // The last hour
        fill = stopMinutes;
      }
    }
    // Case 2: Overnight interval (e.g., 22:15 to 05:30)
    else {
      if (hour < startHour && hour >= end) return { fill: 0, offset: 0 };
      if (hour > startHour || hour < stopHour) {
        fill = 1; // Full hour
      }
      if (hour === startHour) {
        // The first hour
        offset = startMinutes;
        fill = 1 - startMinutes;
      }
      if (hour === stopHour) {
        // The last hour
        fill = stopMinutes;
      }
    }

    if (disabled) {
      fill = 0;
    }

    return { fill: Math.max(0, fill), offset };
  };

  return (
    <Paper component={Stack} withBorder p="xs" w={"100%"} gap={4}>
      {/* Top Section: Visual schedule bars laid out horizontally */}
      <Group wrap="nowrap" gap={0} style={{ flex: 1 }}>
        {hoursOfDay.map((hour) => {
          const { fill, offset } = getFillData(hour, startDecimal, stopDecimal);

          return (
            <Center
              key={`bar-${hour}`}
              flex={1}
              h={25} // Give the timeline a fixed height
              style={{
                position: "relative",
              }}
              px={3}
            >
              {/* 1. Background (always visible) */}
              <Center
                w={"100%"}
                h={"100%"}
                bg={"gray"}
                style={{ borderRadius: 4 }}
                opacity={0.15}
              >
                <IconX color="gray" size={16} />
              </Center>

              {/* 2. Fill bar (overlayed on top) */}
              {fill > 0 && (
                <Box
                  style={{
                    position: "absolute",
                    height: "100%",
                    borderRadius: 4,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    // Apply fill AND offset to width and left position
                    width: `calc((100% - 6px) * ${fill})`,
                    left: `calc(3px + (100% - 6px) * ${offset})`,
                  }}
                  bg={"green.8"}
                />
              )}
            </Center>
          );
        })}
      </Group>

      {/* Bottom Section: Hour labels laid out horizontally */}
      <Group wrap="nowrap" gap={0}>
        {hoursOfDay.map((hour) => (
          <Box key={`label-${hour}`} flex={1} style={{ textAlign: "left" }} pl={4}>
            {/* Only show labels for every other hour to reduce clutter */}
            <Text size="xs" c="dimmed">
              {String(hour).padStart(2, "0")} h
            </Text>
          </Box>
        ))}
      </Group>
    </Paper>
  );
};

export default DailySchedule;
